import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const cases = [
  {
    tag: "Crude Oil",
    title: "15% Cost Reduction for Major Japanese Refiner",
    client: "Leading Japanese Refinery",
    result: "¥4.2B annual savings",
    desc: "A major Japanese refiner engaged Medigreen to renegotiate their long-term crude supply contracts. Through rigorous market analysis and strategic negotiation, we restructured pricing formulas and diversified the supplier base, achieving a 15% reduction in procurement costs.",
    img: "https://public.readdy.ai/ai/img_res/da2c068ef2f44e8802b92995508ff325.jpg",
  },
  {
    tag: "LNG",
    title: "LNG Portfolio Optimization for Korean Utility",
    client: "South Korean Power Utility",
    result: "Supply security enhanced",
    desc: "Medigreen helped a South Korean utility optimize its LNG procurement portfolio by balancing long-term contracts with flexible spot purchases. We negotiated destination flexibility clauses and improved price review mechanisms, significantly enhancing supply security.",
    img: "https://readdy.ai/api/search-image?query=South%20Korea%20LNG%20import%20terminal%20with%20large%20storage%20tanks%20and%20regasification%20facility%2C%20industrial%20energy%20infrastructure%2C%20professional%20photography%2C%20coastal%20setting&width=800&height=500&seq=cs002&orientation=landscape",
  },
  {
    tag: "Strategy",
    title: "New Market Entry Strategy for Southeast Asian Buyer",
    client: "Singapore-based Energy Company",
    result: "3 new supply agreements",
    desc: "A Singapore-based energy company sought to enter the LNG spot market for the first time. Medigreen developed a comprehensive market entry strategy, identified suitable counterparties, and supported negotiations for three initial supply agreements.",
    img: "https://readdy.ai/api/search-image?query=Singapore%20port%20and%20energy%20infrastructure%20with%20modern%20skyline%2C%20industrial%20waterfront%20with%20LNG%20facilities%2C%20professional%20aerial%20photography%2C%20clear%20day&width=800&height=500&seq=cs003&orientation=landscape",
  },
  {
    tag: "Risk Management",
    title: "Supply Disruption Contingency Planning",
    client: "Japanese Industrial Conglomerate",
    result: "Zero supply interruptions",
    desc: "Following geopolitical tensions affecting a key supply route, Medigreen rapidly developed and implemented a contingency procurement plan for a major industrial client, securing alternative supplies with zero interruption to operations.",
    img: "https://readdy.ai/api/search-image?query=global%20energy%20supply%20chain%20logistics%20map%20with%20shipping%20routes%2C%20tanker%20vessels%20at%20sea%2C%20professional%20business%20strategy%20visualization%2C%20world%20map%20background&width=800&height=500&seq=cs004&orientation=landscape",
  },
];

export default function CaseStudiesPage() {
  const { t } = useTranslation(["pages"]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative pt-32 pb-20 bg-slate-900">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=energy%20industry%20success%20business%20meeting%20with%20charts%20showing%20positive%20results%2C%20professional%20corporate%20environment%2C%20modern%20office%2C%20confident%20executives&width=1920&height=600&seq=cshero&orientation=landscape"
            alt="Case Studies"
            className="w-full h-full object-cover object-top opacity-25"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4">
            {t("pages:caseStudies.hero.subtitle")}
          </p>
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("pages:caseStudies.hero.title")}
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            {t("pages:caseStudies.hero.description")}
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-16">
          {cases.map((c, i) => (
            <div
              key={c.title}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "" : ""}`}
            >
              <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="w-full h-72 rounded-xl overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover object-top" />
                </div>
              </div>
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full">
                    {c.tag}
                  </span>
                  <span className="bg-slate-100 text-slate-600 text-xs font-semibold px-3 py-1 rounded-full">
                    {c.client}
                  </span>
                </div>
                <h2
                  className="text-2xl font-bold text-slate-900 mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {c.title}
                </h2>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-trophy-line text-emerald-600" />
                  </div>
                  <span className="text-emerald-700 font-semibold text-sm">{c.result}</span>
                </div>
                <p className="text-slate-600 leading-relaxed">{c.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-emerald-800 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("pages:caseStudies.cta.title")}
          </h2>
          <p className="text-emerald-200 mb-8">{t("pages:caseStudies.cta.description")}</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-emerald-800 font-semibold px-8 py-3.5 rounded-md hover:bg-emerald-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("pages:caseStudies.cta.button")} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}